import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "../api";
import { useAuth } from "../auth";
import { useI18n, formatApiErrorDetail } from "../i18n";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import { LogIn } from "lucide-react";

export default function Login() {
  const { t } = useI18n();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const { data } = await client.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || t.auth.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep flex flex-col">
      <Nav variant="marketing" />
      <div className="flex-1 max-w-md w-full mx-auto px-6 pt-16 pb-24" data-testid="login-page">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3"><Logo size="xl" linked={false} /></div>
          <h1 className="font-heading text-3xl tracking-tight">{t.auth.loginTitle}</h1>
          <p className="text-zinc-400 mt-2 text-sm">{t.auth.loginSub}</p>
        </div>
        <form onSubmit={submit} className="card-neon p-7 space-y-4">
          <Field label={t.auth.email} type="email" value={email} onChange={setEmail} testId="login-email" required />
          <Field label={t.auth.password} type="password" value={password} onChange={setPassword} testId="login-password" required />
          {err && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
            {loading ? t.auth.processing : t.auth.submitLogin}
          </button>
          <div className="flex items-center justify-between text-xs text-zinc-500 pt-2">
            <Link to="/forgot-password" className="hover:text-neon-cyan transition" data-testid="login-forgot">{t.auth.forgotLink}</Link>
            <span>
              {t.auth.noAccount} <Link to="/register" className="text-neon-cyan hover:underline" data-testid="login-to-register">{t.auth.registerCta}</Link>
            </span>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="input-label">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="input-neon" data-testid={testId} />
    </div>
  );
}
